import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  EuiLoadingSpinner,
  EuiPanel,
  EuiText,
  EuiSelect,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFieldText,
  EuiFormRow,
  EuiTextArea,
  EuiComboBox
} from '@elastic/eui';
import '../style.css';
import YouTubeSermonImporter, { getYouTubeVideoId } from './YouTubeSermonImporter';

const normalizeTags = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || '').split(/[,，;；]/).map((item) => item.trim()).filter(Boolean);
};

const SermonManagement = () => {
  const [songs, setSongs] = useState([]);
  const [showSongs, setShowSongs] = useState([]);
  const [invalidSongs, setInvalidSongs] = useState([]);
  const [checkedSongs, setCheckedSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState(null);
  const [sortKey, setSortKey] = useState('title'); // 默认按标题排序
  const [languageOptions, setLanguageOptions] = useState([]);
  const [officialOptions, setOfficialOptions] = useState([]);
  const [resultMessage, setResultMessage] = useState(''); // 用于显示提示信息
  const [isNewSong, setIsNewSong] = useState(false);  // 用于判断是否是新增诗歌
  const [searchTerm, setSearchTerm] = useState(''); // 用于存储搜索关键词
  const [totalCount, setTotalCount] = useState(0);
  const [showYouTubeImporter, setShowYouTubeImporter] = useState(false);
  const [isFetchingYouTubeMetadata, setIsFetchingYouTubeMetadata] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [showCheckResults, setShowCheckResults] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0, elapsedTime: 0 });
  const [startIndex, setStartIndex] = useState(1);
  const [endIndex, setEndIndex] = useState(1);
  const [checkedNum, setCheckedNum] = useState(0);
  const [skippedNum, setSkippedNum] = useState(0);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const fetchAllSongs = async () => {
    const apiURL = process.env.REACT_APP_MasterApiURL;
    const apiKey = process.env.REACT_APP_APIKey;
    const pageSize = 1000;
    let currentPage = 1;
    let hasMoreData = true;

    let fetchedSongs = [];

    try {
      while (hasMoreData) {
        const response = await fetch(apiURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer search-" + apiKey
          },
          body: JSON.stringify({
            query: "",
            filters: { "all": [{ "doctype": "信息" }] },
            page: { size: pageSize, current: currentPage },
            sort: { id: "asc" }
          })
        });

        if (response.ok) {
          const data = await response.json();
          const songs = data.results.map(song => {         
            return {
              id: song.id.raw || "",
              title: song.title.raw || "",
              link: song.link.raw || "",
              doctype: song.doctype.raw || "",
              publisher: song.publisher.raw || "",
              category: song.category.raw || "",
              language: song.language.raw || "",
              description: song.description.raw || "",
              speaker: song.speaker.raw || "",
              tag: song.tag.raw || "",
              official: song.official.raw || "",
              verselist: song.verselist.raw || "",
              viewct: song.viewct.raw || "",
            };
          });

          fetchedSongs = [...fetchedSongs, ...songs];

          if (songs.length < pageSize) {
            hasMoreData = false;
          } else {
            currentPage++;
          }
        } else {
          console.error("Error fetching songs: ", response.statusText);
          return;
        }
      }

      setSongs(fetchedSongs);
      setShowSongs(fetchedSongs);
      setEndIndex(Math.max(1, fetchedSongs.length));
      console.log("All songs fetched:", fetchedSongs);
      setTotalCount(fetchedSongs.length);  // 更新总条数
      // 从所有诗歌中提取唯一的 Language、Tempo 和 Official 选项
      const uniqueLanguages = Array.from(new Set(fetchedSongs.map(song => song.language))).filter(Boolean);
      const uniqueOfficials = Array.from(new Set(fetchedSongs.map(song => song.official))).filter(Boolean);

      // 设置下拉菜单的选项
      setLanguageOptions(uniqueLanguages.map(lang => ({ label: lang })));
      setOfficialOptions(uniqueOfficials.map(official => ({ label: official })));
    } catch (error) {
      console.error("Error while fetching songs: ", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllSongs();
  }, []);


  // 提取ID中的数字部分
  const extractIdNumber = (id) => {
    const match = id.match(/\d+$/);  // 匹配最后的数字部分
    return match ? parseInt(match[0], 10) : id;
  };
  const getAvailableIds = (count = 1) => {
    const usedIds = new Set(
      songs.map((song) => extractIdNumber(String(song.id))).filter(Number.isFinite)
    );
    const availableIds = [];
    let candidate = 1;
    while (availableIds.length < count) {
      if (!usedIds.has(candidate)) availableIds.push(candidate);
      candidate += 1;
    }
    return availableIds;
  };

  const getNextId = () => getAvailableIds(1)[0];

  // 根据搜索关键词过滤列表
  const filteredSongs = showSongs.filter((song) =>
    song.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 按位数排序
  const sortSongs = (songs, key) => {
    if (key === 'id') {
      return [...songs].sort((a, b) => {
        const idA = extractIdNumber(a.id);
        const idB = extractIdNumber(b.id);
        return idA - idB;
      });
    }
    // 如果是按title排序，直接按字母排序
    return [...songs].sort((a, b) => (a[key] > b[key] ? 1 : -1));
  };

  // 对过滤后的结果进行排序
  const sortedSongs = sortSongs(filteredSongs, sortKey);

  const handleSongSelect = (song) => {
      setSelectedSong(song); // 设置选中的歌曲
      setIsNewSong(false);  // 保存成功后，取消新增标识
      setResultMessage(''); // 清空 resultMessage
    };

  const handleSermonCheck = async () => {
    if (startIndex < 1 || endIndex > songs.length || startIndex > endIndex) {
      window.alert('請輸入有效的起始和終止編號。');
      return;
    }

    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY || localStorage.getItem('ctb.youtubeDataApiKey') || '';
    if (!apiKey) {
      window.alert('尚未設定 YouTube 讀取服務，請在前端環境變數中設定 REACT_APP_YOUTUBE_API_KEY。');
      return;
    }

    const sorted = sortSongs(songs, 'id');
    const rangeSongs = sorted.slice(startIndex - 1, endIndex);
    const mappings = rangeSongs.map((song) => ({ song, videoId: getYouTubeVideoId(song.link) }));
    const videoIds = [...new Set(mappings.map((item) => item.videoId).filter(Boolean))];
    const totalBatches = Math.max(1, Math.ceil(videoIds.length / 50));
    const availableVideos = new Map();
    const startedAt = Date.now();

    setShowCheckResults(false);
    setIsChecking(true);
    setProgress({ completed: 0, total: totalBatches, elapsedTime: 0 });

    try {
      for (let index = 0; index < videoIds.length; index += 50) {
        const batch = videoIds.slice(index, index + 50);
        const query = new URLSearchParams({
          part: 'id,status',
          id: batch.join(','),
          key: apiKey,
        });
        const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?${query}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error?.message || `YouTube API 檢查失敗 (${response.status})`);
        }

        (data.items || []).forEach((video) => {
          let reason = '';
          if (!['public', 'unlisted'].includes(video.status?.privacyStatus)) {
            reason = '影片不是公開或不公開連結';
          } else if (video.status?.uploadStatus !== 'processed') {
            reason = `影片尚未處理完成（${video.status?.uploadStatus || '狀態不明'}）`;
          } else if (video.status?.embeddable === false) {
            reason = '影片擁有者禁止站內嵌入播放';
          }
          availableVideos.set(video.id, { isValid: !reason, reason });
        });

        setProgress({
          completed: Math.floor(index / 50) + 1,
          total: totalBatches,
          elapsedTime: Math.floor((Date.now() - startedAt) / 1000),
        });
      }

      if (!videoIds.length) {
        setProgress({ completed: 1, total: 1, elapsedTime: 0 });
      }

      const checked = mappings.map(({ song, videoId }) => {
        if (!videoId) {
          return {
            ...song,
            checkStatus: 'skipped',
            checkReason: '非 YouTube 連結，未檢查',
            invalidReason: '',
          };
        }
        const result = availableVideos.get(videoId) || {
          isValid: false,
          reason: '影片已刪除、設為私人或不存在',
        };
        return {
          ...song,
          isValid: result.isValid,
          checkStatus: result.isValid ? 'valid' : 'invalid',
          checkReason: result.reason,
          invalidReason: result.reason,
        };
      });
      const invalid = checked.filter((song) => song.checkStatus === 'invalid');
      const checkedById = new Map(checked.map((song) => [song.id, song]));
      const updatedSongs = songs.map((song) => checkedById.get(song.id) || song);

      setSongs(updatedSongs);
      setCheckedSongs(checked);
      setInvalidSongs(invalid);
      setCheckedNum(checked.filter((song) => song.checkStatus !== 'skipped').length);
      setSkippedNum(checked.filter((song) => song.checkStatus === 'skipped').length);
      setShowSongs(invalid.length ? invalid : checked);
      setTotalCount(invalid.length ? invalid.length : checked.length);
      setProgress((current) => ({
        ...current,
        elapsedTime: Math.floor((Date.now() - startedAt) / 1000),
      }));
      setShowCheckResults(true);
    } catch (error) {
      console.error('YouTube API 信息檢查失敗:', error);
      window.alert(error.message || 'YouTube API 信息檢查失敗，請稍後再試。');
    } finally {
      setIsChecking(false);
    }
  };
  // 点击"新建诗歌"按钮时调用
  const handleNewSong = () => {
    const newSong = {
      id: getNextId().toString(),  // ID为最大值+1
      title: '',
      link: '',
      publisher: '',
      category: '講道',
      language: '國語',
      description: '',
      speaker: '',
      tag: '',
      official: '',
      verselist: '',
      viewct: '',
    };
    setSelectedSong(newSong);  // 将新诗歌设置为选中的诗歌
    setIsNewSong(true);  // 设置为新增模式
    setResultMessage('');  // 清空结果提示
  };

  const extractScriptureRefs = (text = '') => [...new Set(
    (text.match(/(?:[1-3一二三]?\s*)?[\u3400-\u9fff]{1,8}\s*\d{1,3}\s*[:：]\s*\d{1,3}(?:\s*[-–—至]\s*\d{1,3})?/g) || [])
      .map((value) => value.replace(/\s+/g, ' ').trim())
  )].join(', ');

  const fetchYouTubeVideo = async (part) => {
    const videoId = getYouTubeVideoId(selectedSong?.link);
    if (!videoId) throw new Error('請先輸入有效的 YouTube 影片連結。');
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY || localStorage.getItem('ctb.youtubeDataApiKey') || '';
    if (!apiKey) throw new Error('尚未設定 YouTube 讀取服務，請在前端環境變數中設定 REACT_APP_YOUTUBE_API_KEY。');
    const query = new URLSearchParams({ part, id: videoId, key: apiKey });
    const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?${query}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error?.message || `YouTube API 請求失敗 (${response.status})`);
    const video = data.items?.[0];
    if (!video) throw new Error('找不到這部影片，或影片不是公開的。');
    return video;
  };

  const fillSermonFromYouTube = async () => {
    if (!isNewSong) return setResultMessage('已有信息不會自動覆蓋資料；請使用「更新觀看次數」。');
    setIsFetchingYouTubeMetadata(true);
    setResultMessage('正在讀取 YouTube 影片資料…');
    try {
      const video = await fetchYouTubeVideo('snippet,statistics');
      const snippet = video.snippet || {};
      const description = snippet.description || '';
      const hashtags = description.match(/#[^\s#]+/g) || [];
      setSelectedSong((current) => ({
        ...current,
        title: snippet.title || current.title,
        publisher: snippet.channelTitle || current.publisher,
        description: description || current.description,
        language: snippet.defaultAudioLanguage || snippet.defaultLanguage || current.language,
        tag: hashtags.length ? hashtags.map((tag) => tag.slice(1)).join(', ') : current.tag,
        verselist: extractScriptureRefs(`${snippet.title || ''}\n${description}`) || current.verselist,
        viewct: video.statistics?.viewCount || current.viewct,
      }));
      setResultMessage('已讀取 YouTube 資料，請補充講員並檢查分類、語言、經文和簡介後保存。');
    } catch (error) {
      setResultMessage(error.message || '讀取 YouTube 影片資料失敗。');
    } finally {
      setIsFetchingYouTubeMetadata(false);
    }
  };

  const updateYouTubeViewCount = async () => {
    setIsFetchingYouTubeMetadata(true);
    setResultMessage('正在更新 YouTube 觀看次數…');
    try {
      const video = await fetchYouTubeVideo('statistics');
      const viewCount = video.statistics?.viewCount;
      if (viewCount === undefined) throw new Error('YouTube 沒有返回這部影片的觀看次數。');
      setSelectedSong((current) => ({ ...current, viewct: viewCount }));
      setResultMessage(`觀看次數已更新為 ${Number(viewCount).toLocaleString()}，請點擊「保存修改」寫入資料。`);
    } catch (error) {
      setResultMessage(error.message || '更新 YouTube 觀看次數失敗。');
    } finally {
      setIsFetchingYouTubeMetadata(false);
    }
  };
  // 保存修改的函数
  async function handleSaveSong() {
      // 设置初始提示信息
      setResultMessage("Start updating song...");

        // 验证红框内的项目是否为空
      if (!selectedSong.title || !selectedSong.link || !selectedSong.publisher || !selectedSong.language || !selectedSong.category || !selectedSong.description || !selectedSong.speaker) {
        setResultMessage('Error: All fields must be filled.');
        return;  // 阻止保存操作
      }

      // 验证 link 是否为有效 URL
      const urlPattern = new RegExp('^(https?:\\/\\/)?' + // 协议
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|(\\d{1,3}\\.){3}\\d{1,3})' + // 域名或IP
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // 端口和路径
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // 查询字符串
        '(\\#[-a-z\\d_]*)?$', 'i'); // 片段标识符

      if (!urlPattern.test(selectedSong.link)) {
        setResultMessage('Error: The Song Link must be a valid URL.');
        return;  // 阻止保存操作
      }
        // 如果是新增的诗歌（通过 isNewSong 判断），检查 Song Link 是否已存在
      if (isNewSong) {
        const linkExists = songs.some(song => song.link === selectedSong.link);
        if (linkExists) {
          setResultMessage('Error: The Song Link already exists. Please enter a unique link.');
          return;  // 阻止保存操作
        }
      }
      // 获取表单输入值（使用 EUI 组件的值获取方法）
      const title = selectedSong.title;
      const link = selectedSong.link;
      const songId = extractIdNumber(selectedSong.id);

      // 获取 EuiComboBox 选中的值 (假设是单选)
      const language = selectedSong.language;
      const official = selectedSong.official;

      // 构建新的歌曲对象
      const newSong = {
          id: songId,
          doctype: "信息",
          title: title,
          link: link,
          publisher: selectedSong.publisher,
          category: selectedSong.category,
          language: language,  // 从 EuiComboBox 获取的值
          description: selectedSong.description,  
          speaker: selectedSong.speaker,  // 从 EuiComboBox 获取的值
          tag: normalizeTags(selectedSong.tag),
          official: official,  // 从 EuiComboBox 获取的值
          verselist: selectedSong.verselist,
          viewct: selectedSong.viewct,
      };

      // 调用上传函数并处理响应
      const uploadSuccess = await uploadSong(newSong);

      // 更新提示信息
      if (uploadSuccess) {
          newSong.id = "ctb-sermon|"+songId;
          setSelectedSong(newSong);  // 使用返回的最新数据更新当前选中歌曲
          setResultMessage("Song updated successfully!");
          setSongs((prevSongs) => {
            const songExists = prevSongs.some(song => song.id === newSong.id);
            if (!songExists) {
              return [...prevSongs, newSong];  // 如果不存在，添加新诗歌
            } else {
              // 如果已存在，替换更新后的歌曲
              return prevSongs.map(song => 
                song.id === newSong.id ? newSong : song
              );
            }
          });
          setShowSongs((prevSongs) => {
            const songExists = prevSongs.some(song => song.id === newSong.id);
            return songExists
              ? prevSongs.map(song => song.id === newSong.id ? newSong : song)
              : [...prevSongs, newSong];
          });
          if (isNewSong){
            setTotalCount(totalCount+1);  // 更新总条数
            setEndIndex((current) => current + 1);
          }
          setIsNewSong(false);  // 保存成功后，取消新增标识
      } else {
          setResultMessage("Song update failed.");
      }
  };

  async function uploadSong(song) {
    const postURL = process.env.REACT_APP_SermonDocURL;
    const postKey = process.env.REACT_APP_PostKey;
      try {
          const response = await fetch(postURL, {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  "Authorization": postKey
              },
              body: JSON.stringify([song])  // 上传时将歌曲对象封装为数组
          });

          if (response.ok) {
              console.log("Song uploaded: ", await response.json());
              return true;  // 上传成功
          } else {
              console.error("Error uploading song: ", response.statusText);
              return false;  // 上传失败
          }
      } catch (error) {
          console.error("Error while uploading song: ", error);
          return false;  // 处理异常
      }
  }

  async function handleDeleteInvalidSermon(song, event) {
    event?.stopPropagation();
    if (song?.checkStatus !== 'invalid') return;
    if (!window.confirm(`確定永久刪除失效信息「${song.title}」？刪除後該 ID 可供新項目再次使用。`)) return;

    const songId = extractIdNumber(String(song.id));
    try {
      await axios.delete('/api/admin/search-documents/sermon', {
        headers: {
          'X-Elastic-Authorization': process.env.REACT_APP_PostKey,
        },
        data: { documentIds: [String(songId)] },
      });

      setSongs((current) => current.filter((item) => item.id !== song.id));
      setInvalidSongs((current) => current.filter((item) => item.id !== song.id));
      setCheckedSongs((current) => current.filter((item) => item.id !== song.id));
      setShowSongs((current) => {
        const next = current.filter((item) => item.id !== song.id);
        setTotalCount(next.length);
        return next;
      });
      setEndIndex((current) => Math.max(1, Math.min(current, songs.length - 1)));
      if (selectedSong?.id === song.id) setSelectedSong(null);
      setResultMessage(`已刪除失效信息，ID ${songId} 現可再次使用。`);
    } catch (error) {
      console.error('刪除失效信息失敗:', error);
      window.alert(error?.response?.data?.message || error.message || '刪除失效信息失敗，請稍後再試。');
    }
  }

  async function handleYouTubeImport(importedVideos) {
    const knownVideoIds = new Set(songs.map((song) => getYouTubeVideoId(song.link)).filter(Boolean));
    const uniqueVideos = importedVideos.filter((video) => {
      const videoId = getYouTubeVideoId(video.link);
      if (!videoId || knownVideoIds.has(videoId)) return false;
      knownVideoIds.add(videoId);
      return true;
    });

    if (!uniqueVideos.length) throw new Error('選擇的影片已經全部存在。');

    const availableIds = getAvailableIds(uniqueVideos.length);
    const documents = uniqueVideos.map((video, index) => ({
      ...video,
      id: availableIds[index],
      doctype: '信息',
    }));
    const response = await fetch(process.env.REACT_APP_SermonDocURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_PostKey,
      },
      body: JSON.stringify(documents),
    });

    if (!response.ok) throw new Error(`Elastic 批次寫入失敗 (${response.status})。`);
    const result = await response.json();
    if (Array.isArray(result) && result.some((item) => item?.errors?.length)) {
      console.error('YouTube import document errors:', result);
      throw new Error('部分影片未能寫入 Elastic，請查看瀏覽器記錄。');
    }

    const addedSongs = documents.map((document) => ({ ...document, id: `ctb-sermon|${document.id}` }));
    setSongs((current) => [...current, ...addedSongs]);
    setShowSongs((current) => [...current, ...addedSongs]);
    setTotalCount((current) => current + addedSongs.length);
    setEndIndex((current) => current + addedSongs.length);
    return uniqueVideos.map((video) => getYouTubeVideoId(video.link));
  }

  const formatExportSong = (song) => ({
    id: song.id,
    doctype: song.doctype,
    title: song.title,
    link: song.link,
    publisher: song.publisher,
    category: song.category,
    language: song.language,
    description: song.description,
    speaker: song.speaker,
    tag: normalizeTags(song.tag),
    official: song.official,
    verselist: song.verselist,
    viewct: song.viewct,
    checkStatus: song.checkStatus || '',
    checkReason: song.checkReason || song.invalidReason || '',
  });

  const getExportFileName = (prefix, extension) => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toISOString().slice(11, 19).replace(/:/g, '-');
    return `${prefix}-${date}-${time}.${extension}`;
  };

  const saveAsJson = (songList = songs, prefix = 'sermons') => {
    const sorted = sortSongs(songList, 'id').map(formatExportSong);
    const content = { total: sorted.length, sermons: sorted };
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = getExportFileName(prefix, 'json');
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(url);
  };

  const saveAsExcel = async (songList = songs, prefix = 'sermons') => {
    if (isExportingExcel) return;
    setIsExportingExcel(true);
    try {
      const ExcelJSModule = await import('exceljs');
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'CheckTheBible';
      workbook.created = new Date();
      const worksheet = workbook.addWorksheet('信息', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });
      worksheet.columns = [
        { header: 'ID', key: 'id', width: 18 },
        { header: '類型', key: 'doctype', width: 10 },
        { header: '標題', key: 'title', width: 38 },
        { header: '連結', key: 'link', width: 45 },
        { header: '發佈者', key: 'publisher', width: 24 },
        { header: '分類', key: 'category', width: 18 },
        { header: '語言', key: 'language', width: 12 },
        { header: '簡介', key: 'description', width: 55 },
        { header: '講員', key: 'speaker', width: 20 },
        { header: '標籤', key: 'tag', width: 28 },
        { header: '官方', key: 'official', width: 12 },
        { header: '相關經文', key: 'verselist', width: 30 },
        { header: '觀看數', key: 'viewct', width: 12 },
        { header: '檢查狀態', key: 'checkStatus', width: 14 },
        { header: '檢查結果／失效原因', key: 'checkReason', width: 38 },
      ];

      sortSongs(songList, 'id').map(formatExportSong).forEach((song) => {
        const row = worksheet.addRow({ ...song, tag: song.tag.join(', ') });
        if (song.link) {
          row.getCell('link').value = { text: song.link, hyperlink: song.link };
          row.getCell('link').font = { color: { argb: 'FF0563C1' }, underline: true };
        }
      });
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { vertical: 'middle' };
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) row.alignment = { vertical: 'top', wrapText: true };
      });
      worksheet.autoFilter = { from: 'A1', to: 'O1' };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = getExportFileName(prefix, 'xlsx');
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('信息 Excel 匯出失敗:', error);
      window.alert('信息 Excel 匯出失敗，請稍後再試。');
    } finally {
      setIsExportingExcel(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <EuiLoadingSpinner size="xl" />
        <EuiText color="default" style={{ marginTop: '10px' }}>
          正在加载中...
        </EuiText>
      </div>
    );
  }

  return (
    <EuiFlexGroup>
      {/* 左侧歌曲列表 */}
      <EuiFlexItem grow={false} className="song-list-container">
        <EuiPanel paddingSize="s">
          <EuiFormRow label={`信息列表 共${totalCount}條`}>
            <EuiSelect
              options={[
                { value: 'title', text: '按标题排序' },
                { value: 'id', text: '按ID排序' }
              ]}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            />
          </EuiFormRow>

          {/* 搜索框 */}
          <input
            type="text"
            placeholder="搜索信息..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
          />

          <ul className="song-list">
            {/* {sortSongs(songs, sortKey).map((song) => ( */}
            {sortedSongs.map((song) => (
              <li key={song.id} onClick={() => handleSongSelect(song)}>
                <div>{song.title} (ID: {song.id})</div>
                {song.checkStatus === 'invalid' && (
                  <div style={{ color: '#bd271e', fontSize: '12px', marginTop: '3px' }}>
                    <div>失效原因：{song.invalidReason}</div>
                    <EuiButton
                      size="s"
                      color="danger"
                      iconType="trash"
                      style={{ marginTop: '6px' }}
                      onClick={(event) => handleDeleteInvalidSermon(song, event)}
                    >
                      刪除失效信息
                    </EuiButton>
                  </div>
                )}
                {song.checkStatus === 'skipped' && (
                  <div style={{ color: '#69707d', fontSize: '12px', marginTop: '3px' }}>
                    {song.checkReason}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </EuiPanel>
      </EuiFlexItem>

      {/* 右侧详细信息编辑 */}
      <EuiFlexItem>
        <EuiFlexGroup direction="column">
        {selectedSong ? (
          <EuiPanel paddingSize="l">
            <h3>{isNewSong ? '新建信息' : `編輯信息：${selectedSong.title}`}</h3>

            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiFormRow label="Sermon ID">
                  <EuiFieldText value={extractIdNumber(selectedSong.id)} readOnly />
                </EuiFormRow>

                <EuiFormRow label="Sermon Title">
                  <EuiFieldText
                    value={selectedSong.title}
                    onChange={(e) => setSelectedSong({ ...selectedSong, title: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="信息連結">
                  <EuiFlexGroup gutterSize="s">
                    <EuiFlexItem>
                      <EuiFieldText
                        value={selectedSong.link}
                        onChange={(e) => setSelectedSong({ ...selectedSong, link: e.target.value })}
                      />
                    </EuiFlexItem>
                    {isNewSong ? <EuiFlexItem grow={false}>
                      <EuiButton
                        iconType="logoYoutube"
                        isLoading={isFetchingYouTubeMetadata}
                        onClick={fillSermonFromYouTube}
                      >
                        自動填入
                      </EuiButton>
                    </EuiFlexItem> : <>
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          iconType="popout"
                          href={selectedSong.link || undefined}
                          target="_blank"
                          rel="noreferrer"
                          disabled={!getYouTubeVideoId(selectedSong.link)}
                        >
                          打開連結
                        </EuiButton>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          iconType="refresh"
                          isLoading={isFetchingYouTubeMetadata}
                          disabled={!getYouTubeVideoId(selectedSong.link)}
                          onClick={updateYouTubeViewCount}
                        >
                          更新觀看次數
                        </EuiButton>
                      </EuiFlexItem>
                    </>}
                  </EuiFlexGroup>
                </EuiFormRow>

                <EuiFormRow label="Publisher">
                  <EuiFieldText
                    value={selectedSong.publisher}
                    onChange={(e) => setSelectedSong({ ...selectedSong, publisher: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="Description">
                  <EuiTextArea
                    value={selectedSong.description}
                    onChange={(e) => setSelectedSong({ ...selectedSong, description: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="Language">
                  <EuiComboBox
                    options={languageOptions}
                    selectedOptions={selectedSong.language ? [{ label: selectedSong.language }] : []}
                    onChange={(selected) => {
                      const newLanguage = selected.length ? selected[0].label : '';
                      setSelectedSong({ ...selectedSong, language: newLanguage });
                    }}
                    onCreateOption={(newOption) => {
                      setLanguageOptions([...languageOptions, { label: newOption }]);
                      setSelectedSong({ ...selectedSong, language: newOption });
                    }}
                  />
                </EuiFormRow>
              </EuiFlexItem>

              <EuiFlexItem>
                 <EuiFormRow label="Category">
                  <EuiFieldText
                    value={selectedSong.category}
                    onChange={(e) => setSelectedSong({ ...selectedSong, category: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="Speaker">
                  <EuiFieldText
                    value={selectedSong.speaker}
                    onChange={(e) => setSelectedSong({ ...selectedSong, speaker: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="Viewct">
                  <EuiFieldText
                    value={selectedSong.viewct}
                    onChange={(e) => setSelectedSong({ ...selectedSong, viewct: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="Tags">
                  <EuiFieldText
                    value={Array.isArray(selectedSong.tag) ? selectedSong.tag.join(', ') : selectedSong.tag}
                    onChange={(e) => setSelectedSong({ ...selectedSong, tag: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="Official">
                  <EuiComboBox
                    options={officialOptions}
                    selectedOptions={selectedSong.official ? [{ label: selectedSong.official }] : []}
                    onChange={(selected) => {
                      const newOfficial = selected.length ? selected[0].label : '';
                      setSelectedSong({ ...selectedSong, official: newOfficial });
                    }}
                    onCreateOption={(newOption) => {
                      setOfficialOptions([...officialOptions, { label: newOption }]);
                      setSelectedSong({ ...selectedSong, official: newOption });
                    }}
                  />
                </EuiFormRow>

                <EuiFormRow label="Verselist">
                  <EuiTextArea
                    value={selectedSong.verselist}
                    onChange={(e) => setSelectedSong({ ...selectedSong, verselist: e.target.value })}
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            {/* 显示 resultMessage 的位置 */}
            <div style={{ textAlign: 'center' }}>
              <EuiText color="danger" style={{ marginBottom: '10px' }}>{resultMessage}</EuiText>
              <EuiButton onClick={handleSaveSong}>保存修改</EuiButton>
            </div>
          </EuiPanel>
        ) : (
          <EuiText>请选择一篇信息以查看详细信息。</EuiText>
        )}
          <EuiFlexItem>
            {showCheckResults && (
              <EuiPanel paddingSize="s" style={{ marginBottom: '16px' }}>
                <EuiText>
                  <h3>檢查完成</h3>
                  <p>
                    已檢查 {checkedNum} 條 YouTube 信息，發現 {invalidSongs.length} 條失效；
                    另有 {skippedNum} 條非 YouTube 連結未檢查。耗時 {progress.elapsedTime} 秒。
                  </p>
                </EuiText>
                <EuiFlexGroup responsive={false} gutterSize="s" wrap>
                  <EuiFlexItem grow={false}>
                    <EuiButton onClick={() => { setShowSongs(songs); setTotalCount(songs.length); }}>
                      顯示全部信息
                    </EuiButton>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButton onClick={() => { setShowSongs(checkedSongs); setTotalCount(checkedSongs.length); }}>
                      顯示本次範圍
                    </EuiButton>
                  </EuiFlexItem>
                  {invalidSongs.length > 0 && (
                    <>
                      <EuiFlexItem grow={false}>
                        <EuiButton color="danger" onClick={() => { setShowSongs(invalidSongs); setTotalCount(invalidSongs.length); }}>
                          顯示失效信息
                        </EuiButton>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButton onClick={() => saveAsJson(invalidSongs, 'invalid-sermons')}>
                          保存失效 JSON
                        </EuiButton>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          onClick={() => saveAsExcel(invalidSongs, 'invalid-sermons')}
                          isLoading={isExportingExcel}
                          isDisabled={isExportingExcel}
                        >
                          保存失效 Excel
                        </EuiButton>
                      </EuiFlexItem>
                    </>
                  )}
                </EuiFlexGroup>
              </EuiPanel>
            )}

            <EuiFlexGroup responsive={false} justifyContent="center" gutterSize="s" wrap>
              <EuiFlexItem grow={false}>
                <EuiButton onClick={handleNewSong}>新建信息</EuiButton>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton iconType="logoYoutube" fill onClick={() => setShowYouTubeImporter(true)}>
                  從 YouTube 頻道匯入
                </EuiButton>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton onClick={handleSermonCheck}>信息檢查</EuiButton>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton onClick={() => saveAsJson(songs, 'sermons')}>保存全部 JSON</EuiButton>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton
                  onClick={() => saveAsExcel(songs, 'sermons')}
                  isLoading={isExportingExcel}
                  isDisabled={isExportingExcel}
                >
                  保存全部 Excel
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiPanel paddingSize="s" style={{ marginTop: '16px' }}>
              <EuiText textAlign="center">
                <h3>YouTube 信息檢查範圍</h3>
                <p>依 ID 排序，每次透過 YouTube API 自動以 50 條為一批檢查。</p>
              </EuiText>
              <EuiFlexGroup responsive={false} justifyContent="center" gutterSize="m" wrap>
                <EuiFlexItem grow={false}>
                  <label>
                    起始編號：
                    <input
                      type="number"
                      value={startIndex}
                      min={1}
                      max={Math.max(1, songs.length)}
                      onChange={(event) => setStartIndex(Math.min(Math.max(Number(event.target.value), 1), Math.max(1, songs.length)))}
                      style={{ width: '90px' }}
                    />
                  </label>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <label>
                    終止編號：
                    <input
                      type="number"
                      value={endIndex}
                      min={1}
                      max={Math.max(1, songs.length)}
                      onChange={(event) => setEndIndex(Math.min(Math.max(Number(event.target.value), 1), Math.max(1, songs.length)))}
                      style={{ width: '90px' }}
                    />
                  </label>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
      {showYouTubeImporter && (
        <YouTubeSermonImporter
          existingSongs={songs}
          onClose={() => setShowYouTubeImporter(false)}
          onImport={handleYouTubeImport}
        />
      )}
      {isChecking && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <EuiPanel paddingSize="l" style={{ textAlign: 'center', minWidth: '280px', maxWidth: '480px' }}>
            <EuiLoadingSpinner size="xl" />
            <EuiText>
              <h3>正在檢查信息……</h3>
              <p>已完成 {progress.completed} / {progress.total} 批</p>
            </EuiText>
          </EuiPanel>
        </div>
      )}
    </EuiFlexGroup>
  );
};

export default SermonManagement;
